<template>
  <RevDialog :name="modalName" :title="i18n(translations.title)">
    <template #body>
      <div class="py-24">
        <div class="mb-16 text-center">
          {{ i18n(translations.prompt) }}
        </div>

        <RevButton
          class="mb-8"
          full-width="always"
          :loading="isLoading"
          variant="primary"
          @click="handleConfirm"
        >
          {{ i18n(translations.confirm) }}
        </RevButton>

        <RevButton
          full-width="always"
          variant="secondary"
          @click="handleCancel"
        >
          {{ i18n(translations.cancel) }}
        </RevButton>
      </div>
    </template>
  </RevDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { cancelOrderPickup } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDialog } from '@ds/components/Dialog'
import { closeModal } from '@ds/components/ModalBase'

import type { Order } from '../../../../../../../../Tab.types'

import { MODAL_NAME } from './CancelPickupModal.constants'
import translations from './CancelPickupModal.translations'

const props = defineProps<{ orderId: Order['id']; refreshOrders: () => void }>()

const i18n = useI18n()
const { openSuccessToast, openErrorToast } = useTheToast()

const isLoading = ref(false)
const modalName = computed(() => MODAL_NAME + props.orderId)

async function handleConfirm() {
  try {
    isLoading.value = true

    await $httpFetch(cancelOrderPickup, {
      pathParams: {
        orderId: props.orderId,
      },
      body: {
        orderId: props.orderId,
      },
    })

    openSuccessToast({
      content: i18n(translations.success),
    })

    props.refreshOrders()
    closeModal(modalName.value)
  } catch (error) {
    const formattedError = HttpApiError.fromAnyError(error as Error)
    openErrorToast({
      title: formattedError.title,
      content: formattedError.detail,
    })
  } finally {
    isLoading.value = false
  }
}

function handleCancel() {
  closeModal(modalName.value)
}
</script>
